import React, { useState } from 'react';
import { Container, Row, Col, Card, ListGroup } from 'react-bootstrap';
import { FiClock, FiMail, FiChevronRight } from 'react-icons/fi';

import { THEME } from '../../utils/constants';

export default function PrivacyPolicy() {
    const [activeSection, setActiveSection] = useState('sec-1');

    const customColors = {
        cardBg: '#0d213a',
        borderMuted: 'rgba(255, 255, 255, 0.05)'
    };

    const sections = [
        { id: 'sec-1', title: '1. Information We Collect' },
        { id: 'sec-2', title: '2. How We Use Information' },
        { id: 'sec-3', title: '3. Sharing of Information' },
        { id: 'sec-4', title: '4. Data Security' },
        { id: 'sec-5', title: '5. Cookies' },
        { id: 'sec-6', title: '6. Data Retention' },
        { id: 'sec-7', title: '7. User Rights' },
        { id: 'sec-8', title: '8. Third-Party Links' },
        { id: 'sec-9', title: '9. Changes to Policy' },
        { id: 'sec-10', title: '10. Contact' }
    ];

    const handleScrollTo = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div
            className="position-relative py-5 min-vh-screen"
            style={{ backgroundColor: THEME.colors.secondary, color: THEME.colors.white, overflow: 'hidden' }}
        >
            <div
                className="position-absolute rounded-circle pointer-events-none"
                style={{
                    top: '-10%',
                    right: '5%',
                    width: '500px',
                    height: '500px',
                    background: `radial-gradient(circle, ${THEME.colors.primary}10 0%, transparent 70%)`,
                    filter: 'blur(80px)',
                    zIndex: 1
                }}
            />

            <Container className="position-relative py-5" style={{ zIndex: 2 }}>

                {/* Header Block */}
                <div className="border-bottom pb-4 mb-5" style={{ borderColor: customColors.borderMuted }}>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                            <h1 className="display-4 font-weight-bold text-white tracking-tight mb-2">Privacy Policy</h1>
                            <p className="mb-0" style={{ color: THEME.colors.gray, fontWeight: '300', fontSize: '1.05rem' }}>
                                At Rasops, we are committed to safeguarding your privacy. This Privacy Policy delineates the protocols governing the collection, utilization, and protection of your data.
                            </p>
                        </div>

                        <div
                            className="d-flex align-items-center gap-2 p-3 rounded-3"
                            style={{ backgroundColor: customColors.cardBg, border: `1px solid ${customColors.borderMuted}` }}
                        >
                            <FiClock style={{ color: THEME.colors.primary }} />
                            <small style={{ color: THEME.colors.textMuted, fontWeight: '500' }}>
                                Last Updated: <span className="text-white">24-Mar-2026</span>
                            </small>
                        </div>
                    </div>
                </div>

                <Row className="g-4">

                    {/* Sidebar */}
                    <Col lg={4} className="d-none d-lg-block position-sticky" style={{ top: '2rem', height: 'fit-content' }}>
                        <Card className="border-0 shadow-sm rounded-4" style={{ backgroundColor: customColors.cardBg }}>
                            <Card.Body className="p-3">
                                <p className="text-uppercase tracking-wider px-3 pt-2 font-weight-bold mb-3" style={{ color: THEME.colors.textMuted, fontSize: '0.75rem', letterSpacing: '1.5px' }}>
                                    Document Outline
                                </p>
                                <ListGroup variant="flush" className="bg-transparent border-0 rounded-3 overflow-hidden">
                                    {sections.map((sec) => {
                                        const isActive = activeSection === sec.id;
                                        return (
                                            <ListGroup.Item
                                                key={sec.id}
                                                action
                                                onClick={() => handleScrollTo(sec.id)}
                                                className="border-0 px-3 py-2 d-flex align-items-center justify-content-between text-start transition-all"
                                                style={{
                                                    backgroundColor: isActive ? `${THEME.colors.primary}12` : 'transparent',
                                                    color: isActive ? THEME.colors.primary : THEME.colors.gray,
                                                    fontSize: '0.85rem',
                                                    borderRadius: '8px',
                                                    marginBottom: '2px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <span>{sec.title}</span>
                                                {isActive && <FiChevronRight size={16} />}
                                            </ListGroup.Item>
                                        );
                                    })}
                                </ListGroup>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Policy Text Container */}
                    <Col lg={8}>
                        <Card
                            className="border-0 p-4 p-md-5 shadow-lg rounded-4"
                            style={{ backgroundColor: customColors.cardBg, border: `1px solid ${customColors.borderMuted}`, lineHeight: '1.8' }}
                        >
                            <Card.Body className="p-0 text-white-50" style={{ fontSize: '0.95rem' }}>

                                <div id="sec-1" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">1. Information Collection Protocols</h3>
                                    <p>We process data across three discrete classifications to optimize platform delivery:</p>

                                    <h4 className="h6 mt-3 text-white font-weight-bold">a. Personal Identifiers</h4>
                                    <ul className="ps-3 mb-3">
                                        <li>Customer name</li>
                                        <li>Primary contact phone number</li>
                                    </ul>

                                    <h4 className="h6 mt-3 text-white font-weight-bold">b. Operational & Business Intelligence</h4>
                                    <ul className="ps-3 mb-3">
                                        <li>Commercial establishment and restaurant details</li>
                                        <li>Catalog configurations, digital menus, and pricing structural matrices</li>
                                        <li>Real-time transaction profiles, transactional data, and system order histories</li>
                                    </ul>

                                    <h4 className="h6 mt-3 text-white font-weight-bold">c. Technical & Diagnostic Telemetry</h4>
                                    <ul className="ps-3">
                                        <li>Internet Protocol (IP) addresses</li>
                                        <li>Browser definitions and properties</li>
                                        <li>Device infrastructure hardware logs</li>
                                        <li>Platform access interaction telemetry and usage logs</li>
                                    </ul>
                                </div>

                                <div id="sec-2" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">2. Purpose of Data Utilization</h3>
                                    <p>Collected informational data structures are utilized exclusively for the following functional parameters:</p>
                                    <ul className="ps-3">
                                        <li>Proactive provisioning, maintenance, and execution of the digital core system</li>
                                        <li>Secure transaction parsing, fulfillment, and financial clearing architectures</li>
                                        <li>Transmission of operational lifecycle notifications, alerts, and system deployment releases</li>
                                        <li>Telemetry analytics to scale processing performance and optimize runtime latency</li>
                                        <li>Proactive surveillance to maintain infrastructure security models and mitigate fraudulent activity</li>
                                    </ul>
                                </div>

                                <div id="sec-3" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">3. Information Disclosure & Sharing Boundaries</h3>
                                    <p>Rasops does not engage in open marketplace monetization of enterprise data. Information disclosures are restricted to:</p>
                                    <ul className="ps-3">
                                        <li>Verified third-party payment gateway operators strictly for handling transaction authorization and validation metrics.</li>
                                        <li>Competent statutory, judicial, or regulatory legal authorities under mandated legal enforcement requirements.</li>
                                    </ul>
                                </div>

                                <div id="sec-4" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">4. Data Security Standardizations</h3>
                                    <p>
                                        We deploy robust administrative and cryptographic safety standards to safeguard your organizational data sets against unauthorized intrusion. Notwithstanding these security measures, clients recognize that no digital environment can be certifiably insulated against absolute vulnerability risks.
                                    </p>
                                </div>

                                <div id="sec-5" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">5. Cookie Policies & Session Storage</h3>
                                    <p>
                                        The system implements functional tracking cookies to sustain secure session tokens, store active navigation settings, and capture interaction diagnostics. Users retain autonomous authority to toggle or terminate cookie storage matrices within their software terminal setups.
                                    </p>
                                </div>

                                <div id="sec-6" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">6. Data Retention Life Cycles</h3>
                                    <p>
                                        Your institutional operational profiles are retained within production nodes exclusively for the temporal duration required to manage subscription software delivery, or as mandated to fulfill legal, tax, and auditing regulatory rules.
                                    </p>
                                </div>

                                <div id="sec-7" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">7. Autonomous User Rights</h3>
                                    <p>Platform registrants have the following standard compliance permissions:</p>
                                    <ul className="ps-3">
                                        <li>The right to demand formal compliance audits and view stored identity metrics.</li>
                                        <li>The right to demand prompt modification, correction, or permanent platform extraction (deletion) of asset rows.</li>
                                    </ul>
                                </div>

                                <div id="sec-8" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">8. Third-Party Hyperlinks & External Boundaries</h3>
                                    <p>
                                        The operating space may reference hyperlinked networks governed by external merchant configurations. Rasops exercises no operational oversight over, and assumes no liability for, the data processing behaviors or privacy rules of external networks.
                                    </p>
                                </div>

                                <div id="sec-9" className="mb-5" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">9. Structural Variations to Policy</h3>
                                    <p>
                                        We reserve the right to periodically adjust or replace sections within this privacy architecture. Continued application of platform integrations following visible updates establishes definitive customer consensus to the modified terms.
                                    </p>
                                </div>

                                <div id="sec-10" style={{ scrollMarginTop: '2rem' }}>
                                    <h3 className="h4 text-white font-weight-bold mb-3">10. Administrative Contact Information</h3>
                                    <p>For processing legal verification inquiries, exercising data rights, or coordinating protection parameters, contact the privacy administrative center:</p>

                                    <div
                                        className="d-flex align-items-center gap-3 p-3 rounded-3 mt-3"
                                        style={{ backgroundColor: THEME.colors.secondary, border: `1px solid ${customColors.borderMuted}` }}
                                    >
                                        <div className="p-2 rounded-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: `${THEME.colors.primary}12`, color: THEME.colors.primary }}>
                                            <FiMail size={20} />
                                        </div>
                                        <div>
                                            <small className="d-block text-uppercase tracking-wider font-weight-bold" style={{ fontSize: '0.65rem', color: THEME.colors.textMuted }}>Legal Risk Desk</small>
                                            <a href="mailto:rasops.info@gmail.com" className="font-weight-medium text-decoration-none" style={{ color: THEME.colors.white, transition: 'color 0.2s' }}>
                                                rasops.info@gmail.com
                                            </a>
                                        </div>
                                    </div>
                                </div>

                            </Card.Body>
                        </Card>
                    </Col>

                </Row>
            </Container>
        </div>
    );
}
